import { Component, OnInit, inject, signal, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MiddlewareApiService } from '../api/middleware-api.service';
import { SiteService } from '../site-selector/site.service';
import { PoiDetailService } from '../poi-detail.service';
import { TranslatePipe } from '../pipes/translate.pipe';
import { TranslationService } from '../services/translation.service';

// Tree node types
export type NodeType = 'building' | 'sector' | 'floor' | 'room' | 'functionalArea';

export interface TreeNode {
  id: number;
  name: string;
  code: string;
  description?: string;
  purpose?: string;
  area?: number;
  type: NodeType;
  parentId?: number;
  children?: TreeNode[];
  expanded: boolean;
  loading: boolean;
  loaded: boolean;
}

export interface LocationSelectedEvent {
  type: NodeType;
  id: number;
  code: string;
  name: string;
  description?: string;
  purpose?: string;
  area?: number;
}

@Component({
  selector: 'app-fms-tree',
  standalone: true,
  imports: [CommonModule, TranslatePipe],
  template: `
    <div class="fms-tree" id="fmsTreePanel">
      @if (error()) {
        <div class="tree-error">
          {{ error() }}
        </div>
      }

      <ul class="tree-root">
        @for (node of buildings(); track node.id) {
          <ng-container *ngTemplateOutlet="nodeTemplate; context: { node: node }"></ng-container>
        }

        @if (loading()) {
          <li class="tree-loading">{{ 'loading' | translate:'Načítání' }}...</li>
        }

        @if (!loading() && buildings().length === 0 && !error()) {
          <li class="tree-empty">{{ 'no_buildings_found' | translate:'Nenalezeny žádné budovy' }}</li>
        }
      </ul>
    </div>

    <ng-template #nodeTemplate let-node="node">
      <li class="tree-node">
        <!-- Expand/collapse toggle (matches legacy: +/- text with blue color when folded) -->
        @if (hasChildren(node)) {
          <span class="toggle clickable"
                [style.color]="node.expanded ? '#ccc' : '#0091D7'"
                (click)="toggleNode(node, $event)">{{ node.expanded ? '-' : '+' }}</span>
        }

        <!-- Node label -->
        <span class="clickable node-label"
              [class.selected]="selectedNodeId() === node.id && selectedNodeType() === node.type"
              [title]="getNodeTooltip(node)"
              (click)="onNodeClick(node)">
          {{ getNodeLabel(node) }}
        </span>

        <!-- Children -->
        @if (node.expanded && node.children?.length) {
          <ul>
            @for (child of node.children; track child.id) {
              <ng-container *ngTemplateOutlet="nodeTemplate; context: { node: child }"></ng-container>
            }
          </ul>
        }
      </li>
    </ng-template>
  `,
  styles: [`
    /* Match legacy treeview.panel.scss styles */
    .fms-tree {
      overflow-y: auto;
      margin-left: 10px;
      margin-top: 10px;
      font-family: 'SKODA Next', Arial, sans-serif;
    }

    .clickable {
      font-size: 16px;
    }

    .tree-error {
      padding: 10px;
      color: #ef4444;
      font-size: 14px;
    }

    .tree-loading,
    .tree-empty {
      padding: 10px 0;
      color: #ccc;
      font-style: italic;
    }

    ul {
      margin: 0;
      padding: 0;
      list-style-type: none;
    }

    li {
      padding-left: 20px;
      padding-bottom: 3px;
      padding-top: 3px;
    }

    /* First level items have no left padding */
    .tree-root > li {
      padding-left: 0;
    }

    span.clickable {
      cursor: pointer;
      margin-left: 5px;
    }

    .node-label.selected {
      color: #0091D7;
      font-weight: bold;
    }

    .node-label:hover {
      text-decoration: underline;
    }
  `]
})
export class FmsTreeComponent implements OnInit {
  private api = inject(MiddlewareApiService);
  private siteService = inject(SiteService);
  private poiDetailService = inject(PoiDetailService);
  private translationService = inject(TranslationService);

  // Outputs
  locationSelected = output<LocationSelectedEvent>();

  // State
  buildings = signal<TreeNode[]>([]);
  loading = signal(false);
  error = signal<string | null>(null);
  selectedNodeId = signal<number | null>(null);
  selectedNodeType = signal<NodeType | null>(null);

  ngOnInit(): void {
    this.loadBuildings();
  }

  private loadBuildings(): void {
    this.loading.set(true);
    this.error.set(null);

    const siteId = this.siteService.selectedSite?.id;
    if (!siteId) return;

    this.api.getBuildings(siteId).subscribe({
      next: (buildings) => {
        const nodes: TreeNode[] = buildings.map((b) => ({
          id: b.id,
          name: b.name,
          code: b.code,
          description: b.description,
          type: 'building' as NodeType,
          expanded: false,
          loading: false,
          loaded: false,
        }));

        // Sort by name
        nodes.sort((a, b) => a.name.localeCompare(b.name));
        this.buildings.set(nodes);
        this.loading.set(false);
      },
      error: (err) => {
        console.error('[FmsTree] Error loading buildings:', err);
        this.error.set(this.translationService.translate('buildings_load_error', 'Nepodařilo se načíst budovy'));
        this.loading.set(false);
      },
    });
  }

  getNodeLabel(node: TreeNode): string {
    switch (node.type) {
      case 'building':
        return node.description ? `${node.name} ${node.description}` : node.name;
      case 'room':
      case 'functionalArea':
        return node.purpose ? `${node.name} ${node.purpose}` : node.name;
      default:
        return node.name;
    }
  }

  getNodeTooltip(node: TreeNode): string {
    const label = this.getNodeLabel(node);
    return node.code ? `${node.code} - ${label}` : label;
  }

  hasChildren(node: TreeNode): boolean {
    // Only FunctionalAreas don't have children
    return node.type !== 'functionalArea';
  }

  toggleNode(node: TreeNode, event: Event): void {
    event.stopPropagation();

    if (node.expanded) {
      // Collapse
      node.expanded = false;
      this.updateNode(node);
    } else {
      // Expand - load children if not loaded
      if (!node.loaded) {
        this.loadChildren(node);
      } else {
        node.expanded = true;
        this.updateNode(node);
      }
    }
  }

  private loadChildren(node: TreeNode): void {
    node.loading = true;
    this.updateNode(node);

    const processResults = (items: { id: number; name: string; code: string; purpose?: string; description?: string; area?: number }[], childType: NodeType): void => {
      const children: TreeNode[] = items.map((item) => ({
        id: item.id,
        name: item.name,
        code: item.code,
        purpose: item.purpose,
        description: item.description,
        area: item.area,
        type: childType,
        parentId: node.id,
        expanded: false,
        loading: false,
        loaded: false,
      }));

      // Sort by name
      children.sort((a, b) => a.name.localeCompare(b.name));

      node.children = children;
      node.loading = false;
      node.loaded = true;
      node.expanded = true;
      this.updateNode(node);
    };

    const handleError = (err: unknown): void => {
      console.error(`[FmsTree] Error loading children for ${node.type}:`, err);
      node.loading = false;
      this.updateNode(node);
    };

    switch (node.type) {
      case 'building':
        this.api.getSectors(node.id).subscribe({
          next: (items) => processResults(items, 'sector'),
          error: handleError,
        });
        break;
      case 'sector':
        this.api.getFloors(node.id).subscribe({
          next: (items) => processResults(items, 'floor'),
          error: handleError,
        });
        break;
      case 'floor':
        this.api.getRooms(node.id).subscribe({
          next: (items) => processResults(items, 'room'),
          error: handleError,
        });
        break;
      case 'room':
        this.api.getFunctionalAreas(node.id).subscribe({
          next: (items) => processResults(items, 'functionalArea'),
          error: handleError,
        });
        break;
    }
  }

  onNodeClick(node: TreeNode): void {
    console.log('[FmsTree] Node clicked:', node.type, node.id, node.name);

    this.selectedNodeId.set(node.id);
    this.selectedNodeType.set(node.type);

    // Clicking the label also toggles the subtree, same as legacy showFmsInfo
    if (this.hasChildren(node)) {
      if (!node.loaded) {
        this.loadChildren(node);
      } else {
        node.expanded = !node.expanded;
        this.updateNode(node);
      }
    }

    this.locationSelected.emit({
      type: node.type,
      id: node.id,
      code: node.code,
      name: node.name,
      description: node.description,
      purpose: node.purpose,
      area: node.area,
    });

    // Building and FunctionalArea have POIs: open the info panel and navigate
    // to the POI on map. Other levels are handled by the app component
    // (info panel without map localization), same as legacy.
    const siteId = this.siteService.selectedSite?.id;
    if (!siteId) return;

    if (node.type === 'building') {
      this.poiDetailService.selectBuilding(siteId, node.id);
    } else if (node.type === 'functionalArea') {
      this.poiDetailService.selectFunctionalArea(siteId, node.id);
    }
  }

  private updateNode(node: TreeNode): void {
    // Trigger change detection by updating the buildings array
    this.buildings.update((nodes) => [...nodes]);
  }
}
